import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  type TableState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import {
  ArrayParam,
  NumberParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import PageSizeSelector from "common/components/DataTableV2/PageSizeSelector";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import Pagination from "common/components/ui/Pagination";
import { PAGE_SIZE_OPTIONS } from "common/constants";
import { getPaginationInfoText } from "common/helpers/utils";

import ColumnsVisibilityFilter from "./ColumnsVisibilityFilter";

interface DataTableProps<TData extends { id: string }, TValue> {
  id: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  selectedIds: string[];
  onRowClick: (rowValue: TData) => void;
  //if this is not passed, selection is disabled
  onRowSelectionChange?: (selectedRows: TData[]) => void;
}

export default function DataTableV2<TData extends { id: string }, TValue>({
  id,
  columns,
  data,
  isLoading,
  selectedIds,
  onRowClick,
  onRowSelectionChange,
}: DataTableProps<TData, TValue>) {
  const visibilityStateKey = `table-${id}/columns`;
  const pageSizeKey = `table-${id}/pageSize`;

  // Get default page size from localStorage or fallback to PAGE_SIZE_OPTIONS
  const getStoredPageSize = () => {
    const storedPageSize = localStorage.getItem(pageSizeKey);

    if (storedPageSize) {
      const parsedPageSize = Number(storedPageSize);

      // Validate that the stored value is in PAGE_SIZE_OPTIONS
      if (PAGE_SIZE_OPTIONS.includes(parsedPageSize)) {
        return parsedPageSize;
      }
    }

    return PAGE_SIZE_OPTIONS[2];
  };

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 1), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });

  const [pageSize, setPageSize] = useQueryParam(
    "pageSize",
    withDefault(NumberParam, getStoredPageSize()),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [sort, setSort] = useQueryParam("sort", withDefault(ArrayParam, []), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const localStorageData = localStorage.getItem(visibilityStateKey);
    if (localStorageData) {
      return JSON.parse(localStorageData);
    }
    return columns.reduce((accumulator, { id, enableHiding }) => {
      if (enableHiding !== false) {
        accumulator[id] = true;
      }
      return accumulator;
    }, {} as VisibilityState);
  });

  const sorting: SortingState = useMemo(
    () =>
      sort
        .map((sortItem: string) => {
          const [id, order] = sortItem.split(":");
          return { id, desc: order === "desc" };
        })
        .filter(({ id }) => id),
    [sort],
  );

  // Convert 1-based page to 0-based pageIndex for react-table
  const pageIndex = Math.max(0, page - 1);

  const rowSelection = useMemo(() => {
    return selectedIds.reduce(
      (acc, id) => {
        acc[id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [selectedIds]);

  const state: Partial<TableState> = {
    sorting,
    pagination: { pageIndex: isLoading ? 0 : pageIndex, pageSize },
    rowSelection,
    columnVisibility,
  };

  const reactTableOptions: TableOptions<TData> = {
    data: isLoading || !data ? new Array(pageSize).fill({}) : data,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: (updaterOrValue) => {
      const newVisibilityState =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnVisibility)
          : updaterOrValue;

      setColumnVisibility(newVisibilityState);

      localStorage.setItem(
        visibilityStateKey,
        JSON.stringify(newVisibilityState),
      );
    },
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      const sortArray = newSorting.map(
        ({ id, desc }) => `${id}:${desc ? "desc" : "asc"}`,
      );

      setSort(sortArray);
    },
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === "function" ? updater(rowSelection) : updater;

      const selectedRows = data.filter(({ id }) => newSelection[id]);

      onRowSelectionChange(selectedRows);
    },
    autoResetPageIndex: false,
    onPaginationChange: (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === "function"
          ? updaterOrValue({ pageIndex, pageSize })
          : updaterOrValue;

      // Convert 0-based pageIndex back to 1-based page number
      setPage(newPagination.pageIndex + 1);
    },
    state,
  };

  const table = useReactTable(reactTableOptions);

  const {
    getState,
    getHeaderGroups,
    getRowModel,
    getRowCount,
    getAllColumns,
    firstPage,
    getIsAllPageRowsSelected,
    getIsSomePageRowsSelected,
    getIsSomeRowsSelected,
    getIsAllRowsSelected,
    toggleAllPageRowsSelected,
    toggleAllRowsSelected,
  } = table;

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const currentPage = getState().pagination.pageIndex;
      const currentFirstItem = currentPage * pageSize;
      const newPage = Math.floor(currentFirstItem / newPageSize);

      // Store the new page size in localStorage
      localStorage.setItem(pageSizeKey, newPageSize.toString());

      setPageSize(newPageSize);
      setPage(newPage + 1); // Convert to 1-based
    },
    [pageSize],
  );

  if (getRowModel().rows.length === 0 && getRowCount() !== 0 && !isLoading) {
    firstPage();
  }

  let selectAllCheckboxState: "indeterminate" | boolean =
    getIsAllPageRowsSelected();

  if (getIsSomePageRowsSelected()) {
    selectAllCheckboxState = "indeterminate";
  }

  const shouldSelectAllRows =
    getIsAllPageRowsSelected() && !getIsAllRowsSelected();

  const itemCount = data ? data.length : 0;

  return (
    <>
      <table className="mb-auto border-separate border-spacing-0 overflow-hidden border border-black-200 rounded-3xl">
        <thead>
          {getHeaderGroups().map((headerGroup) => {
            const thStyles =
              "text-left text-button-xs text-black-500 pl-2.5 first:pl-3 py-1.5 border-b border-black-200 last:text-right last:pr-1.5";

            const selectedRowsCount = table.getSelectedRowModel().rows.length;
            const totalRowCount = table.getCoreRowModel().rows.length;

            return (
              <tr key={headerGroup.id}>
                {onRowSelectionChange && (
                  <th className={thStyles}>
                    <Checkbox
                      checked={selectAllCheckboxState}
                      aria-label="Select all"
                      onCheckedChange={() => {
                        toggleAllPageRowsSelected(!selectAllCheckboxState);
                      }}
                    />
                  </th>
                )}

                <th className={thStyles}>
                  {onRowSelectionChange ? (
                    <div className="flex items-center gap-x-1">
                      <p>
                        <span
                          className={selectedRowsCount > 0 && "text-black-950"}
                        >
                          {selectedRowsCount}
                        </span>{" "}
                        of <span>{totalRowCount}</span> selected
                      </p>

                      <RenderIf
                        condition={
                          getIsAllPageRowsSelected() || getIsSomeRowsSelected()
                        }
                      >
                        <Button
                          size="sm"
                          variant="link-purple"
                          onClick={() =>
                            toggleAllRowsSelected(shouldSelectAllRows)
                          }
                        >
                          {shouldSelectAllRows
                            ? "Select all"
                            : "Clear selection"}
                        </Button>
                      </RenderIf>
                    </div>
                  ) : (
                    flexRender(
                      headerGroup.headers[0].column.columnDef.header,
                      headerGroup.headers[0].getContext(),
                    )
                  )}
                </th>

                {headerGroup.headers.slice(1, -1).map((header) => (
                  <th key={header.id} className={thStyles}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}

                <th className={thStyles}>
                  <ColumnsVisibilityFilter allColumns={getAllColumns()} />
                </th>
              </tr>
            );
          })}
        </thead>

        <tbody>
          {getRowModel().rows.map((row) => (
            <tr
              onClick={() => onRowClick(row.original)}
              className={clsx(
                "group cursor-pointer transition-[background]",
                row.getIsSelected()
                  ? "bg-purple-50"
                  : "hover:bg-black-50 bg-white",
              )}
              key={row.id}
            >
              {onRowSelectionChange && (
                <td className="pl-3 w-4 py-3 border-b border-black-200 group-last:border-none">
                  <Checkbox
                    checked={row.getIsSelected()}
                    aria-label="Select account"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onCheckedChange={() => row.toggleSelected()}
                  />
                </td>
              )}

              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="pl-2.5 last:text-right py-3 border-b border-black-200 group-last:border-none last:pr-3"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between mt-4">
        <PageSizeSelector
          pageSize={pageSize}
          setPageSize={(newPageSize) => handlePageSizeChange(newPageSize)}
        />

        <Pagination
          setPage={(newPageIndex) => setPage(newPageIndex + 1)} // Convert pageIndex to page number
          selectedIndex={getState().pagination.pageIndex}
          pageSize={pageSize}
          itemCount={itemCount}
          isLastPageHidden={false}
        />

        <span className="text-xs-medium text-black-600">
          {getPaginationInfoText(pageIndex, pageSize, itemCount)}
        </span>
      </div>
    </>
  );
}
